import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { summaryNotesSchema } from '../schema';
import { useUpdateSummaryNotesMutation } from '@common/cache';
import { paths } from '@common/types';
import { formatApiError } from '@common/errors/error-formatting';
import { toast } from 'react-toastify';
import { PatientContext } from '@views/pages/Patient/context';
import { InferType } from 'yup';
import { useBlocker } from 'react-router-dom';

type IFormShape = InferType<typeof summaryNotesSchema>;

const initialValues = {
  notes: '',
};

export const useSummaryNotes = (
  patient: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json'],
) => {
  const { mutateAsync, isPending } = useUpdateSummaryNotesMutation(patient.id);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const {
    setIsFormValid,
    setTriggerTabChange,
    triggerTabChange,
    handleClearTrigger,
  } = useContext(PatientContext);

  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(summaryNotesSchema),
    defaultValues: {
      ...initialValues,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
    getValues,
  } = formMethods;

  const blocker = useBlocker(
    useCallback(() => {
      return isDirty;
    }, [isDirty]),
  );

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: IFormShape) => {
    try {
      await mutateAsync({
        notes: values.notes || '',
      });

      handleClose();
      toast.success('Saved changes to summary notes');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      if (blocker.state === 'blocked') {
        blocker.proceed();
      }
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleClearTrigger();
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormValid(true);
    reset({ notes: patient.notes });
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const handleSaveChanges = async () => {
    onSubmit();
    reset(getValues());
    setIsFormValid(true);
  };

  useEffect(() => {
    reset({ notes: patient.notes });
  }, [patient.notes]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setIsSaveChangesModalOpen(true);
      setTriggerTabChange(false);
    }
  }, [blocker]);

  useEffect(() => {
    if (isDirty && triggerTabChange) {
      setIsSaveChangesModalOpen(true);
      setTriggerTabChange(false);
      return;
    }

    if (isDirty) {
      setIsFormValid(false);
      return;
    }

    setIsFormValid(isValid);
  }, [isValid, triggerTabChange, isDirty]);

  return {
    formMethods,
    onSubmit,
    isOpen,
    handleOpen,
    handleClose,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
